import { applyIntakeContactPolicy } from "./intake-contact-policy";
import type { QuestionnaireDefinition } from "./schema";
import { hoistStartPages } from "./intake-page";
import { mapIntakeField } from "./intake-adapter-field";
export type IntakeAdapterField = {
  id: string;
  fieldKey: string;
  label: string;
  kind: string;
  required: boolean;
  bindingKey?: string | null;
  options?: unknown;
  allowMultiple?: boolean;
  settings?: unknown;
};

export type IntakeAdapterSection = {
  id: string;
  name: string;
  description?: string | null;
  fields: IntakeAdapterField[];
};

export function intakeFormToQuestionnaire(input: {
  formId: string;
  version: number | string;
  title: string;
  description?: string | null;
  sections: IntakeAdapterSection[];
  excludeFieldKeys?: string[];
}): QuestionnaireDefinition {
  const excluded = new Set(input.excludeFieldKeys ?? []);
  const sections = hoistStartPages<IntakeAdapterField, IntakeAdapterSection>(
    input.sections,
    (fields) => ({
      id: "page_section_start",
      name: "Welcome",
      fields,
    }),
  );
  const steps = sections.flatMap((section) =>
    stepsForSection(section, excluded),
  );

  if (steps.length === 0) {
    steps.push({
      id: "intake:complete",
      title: "Profile complete",
      description: "No additional questions are required.",
      stage: "Profile",
      fields: [
        {
          id: "intake_complete",
          binding: "__intake.complete",
          label: "Profile complete",
          kind: "computed_display",
          calculationKey: "intake.complete",
          required: false,
          sensitivity: "standard",
        },
      ],
    });
  }

  return applyIntakeContactPolicy({
    id: `intake:${input.formId}`,
    version: String(input.version),
    title: input.title,
    description: input.description,
    initialStepId: steps[0]?.id,
    steps,
    metadata: { profile: "intake" },
  });
}

const CONTACT_STEP_TITLE = "How can we reach you?";

function stepsForSection(section: IntakeAdapterSection, excluded: Set<string>) {
  const steps: Array<{
    id: string;
    title: string;
    description?: string | null;
    stage: string;
    fields: NonNullable<ReturnType<typeof mapIntakeField>>[];
  }> = [];
  let contactBatch: IntakeAdapterField[] = [];

  const flushContact = () => {
    if (contactBatch.length === 0) return;
    const fields = contactBatch.flatMap((field) => {
      const mapped = mapIntakeField(field);
      return mapped ? [mapped] : [];
    });
    const anchor =
      contactBatch.find((field) => field.fieldKey === "email") ??
      contactBatch[0];
    if (fields.length > 0 && anchor) {
      steps.push({
        id: `intake:${anchor.id}`,
        title: fields.length > 1 ? CONTACT_STEP_TITLE : anchor.label,
        description: section.description,
        stage: section.name,
        fields,
      });
    }
    contactBatch = [];
  };

  for (const field of section.fields) {
    if (excluded.has(field.fieldKey)) continue;
    if (isReachableContactField(field)) {
      contactBatch.push(field);
      continue;
    }
    flushContact();
    const mapped = mapIntakeField(field);
    if (!mapped) continue;
    steps.push({
      id: `intake:${field.id}`,
      title: field.label,
      description: section.description,
      stage: section.name,
      fields: [mapped],
    });
  }
  flushContact();
  return steps;
}

function isReachableContactField(field: IntakeAdapterField): boolean {
  return (
    field.kind === "contact" &&
    (field.fieldKey === "full_name" ||
      field.fieldKey === "email" ||
      field.fieldKey === "phone" ||
      field.bindingKey === "contact.fullName" ||
      field.bindingKey === "contact.email" ||
      field.bindingKey === "contact.phone")
  );
}
